/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module DataGridModule
 */

import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
    TemplateRef,
    ViewChild,
} from '@angular/core';

import {
    IAviCollectionDataGridRow,
} from '../avi-collection-data-grid/avi-collection-data-grid.types';

import {
    IAviMetricsCollectionDataGridConfig,
} from './avi-metrics-collection-data-grid.types';

import './avi-metrics-collection-data-grid.component.less';

/**
 * Config type for a collection field to be subscribed to a data source.
 * @param id - field name
 * @param subscriber - the component ID where the field is subscribing from, usually the grid ID
 */
interface IDataSourceFieldConfig {
    id: string;
    subscriber: string;
}

/**
 * @description
 *     A collection grid component with metrics data, built on top of AviCollectionDataGrid.
 * @author Zhiqian Liu
 */
@Component({
    selector: 'avi-metrics-collection-data-grid',
    templateUrl: './avi-metrics-collection-data-grid.component.html',
})
export class AviMetricsCollectionDataGridComponent implements AfterViewInit, OnDestroy {
    /**
     * Grid configuration object containing getUniqueRowId, multipleactions, etc.
     * TODO: change to use input transform after Angular v16
     */
    @Input()
    public set config(config: IAviMetricsCollectionDataGridConfig) {
        this.setConfigWithMetrics(config);
    }

    /**
     * Grid Title sent to the grid.
     */
    @Input()
    public gridTitle?: string;

    /**
     * Fire on row selection change to keep the output consistent with avi-collection-data-grid.
     */
    @Output()
    public onSelectionChange = new EventEmitter<IAviCollectionDataGridRow[]>();

    /**
     * Template ref for metric data.
     */
    @ViewChild('metricsCellTemplateRef')
    public metricsCellTemplateRef: TemplateRef<HTMLElement>;

    /**
     * Updated config that includes the template ref of Metrics Cell.
     */
    public configWithMetrics: IAviMetricsCollectionDataGridConfig;

    /** @override */
    public ngAfterViewInit(): void {
        this.setConfigWithMetrics(this.configWithMetrics);
    }

    /**
     * Called on row selection change.
     * Help as a relay to bubble up the onSelectionChange output all the way from
     * avi-data-grid-base.
     */
    public onRowSelectionChange(rows: IAviCollectionDataGridRow[]): void {
        this.onSelectionChange.emit(rows);
    }

    /** @override */
    public ngOnDestroy(): void {
        this.unsubscribeMetricsFields();
    }

    /**
     * Generate a config object with the metrics cell template ref according to the config
     * passed in, and set this.configWithMetrics to it.
     */
    private setConfigWithMetrics(config: IAviMetricsCollectionDataGridConfig): void {
        this.unsubscribeMetricsFields();

        // update reference to trigger proper change detections for the child
        // avi-collection-data-grid component
        const newConfig = { ...config };
        const metricsIdsToSubscribe = new Set<string>();

        // safeguard, metrics cell template ref is ready only after the view has been initiated
        if (this.metricsCellTemplateRef) {
            newConfig.fields.forEach(field => {
                const { id, isMetrics } = field;

                if (isMetrics) {
                    field.templateRef = this.metricsCellTemplateRef;

                    metricsIdsToSubscribe.add(id);
                }
            });
        }

        this.configWithMetrics = newConfig;

        // subscribe the new metrics fields to the collection data source for loading
        this.configWithMetrics.collection.subscribe(
            Array.from(metricsIdsToSubscribe.values())
                .map(id => this.getDataSourceFieldConfig(id)),
            // "false" to trigger data source loading after subscribing
            false,
        );
    }

    /**
     * Unsubscribe the metrics fields from collection data source.
     */
    private unsubscribeMetricsFields(): void {
        if (this.configWithMetrics) {
            const { fields, collection } = this.configWithMetrics;

            fields.forEach(({ id, isMetrics }) => {
                if (isMetrics) {
                    // the second param (dontLoad) of unSubscribe() defaults to true so that the
                    // unsubscriptions don't cause unnecessary collection reloadings
                    collection.unSubscribe(this.getDataSourceFieldConfig(id));
                }
            });
        }
    }

    /**
     * Getter for grid ID from the config or collection ID.
     */
    private get gridId(): string | undefined {
        return this.configWithMetrics?.id ||
            this.configWithMetrics?.collection?.objectName ||
            undefined;
    }

    /**
     * Generate and return an config object for a collection field to be subscribed to a data
     * source.
     */
    private getDataSourceFieldConfig(fieldId: string): IDataSourceFieldConfig {
        return {
            id: fieldId,
            subscriber: this.gridId,
        };
    }
}
